import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { baueStammdaten } from "../daten/vomServer";
import { useAnmeldung } from "./Anmeldung";
import * as server from "../dienst/server";

/*
  Zustand der Demo.

  Zwei Quellen laufen hier zusammen:

  1. Sitzung, Tagesordnung und Vorlagen holt die App beim Start vom Server. Sie
     stammen aus dem Ratsinformationssystem der Verwaltung. Ohne sie geht
     nichts — deshalb wartet die Anwendung darauf und sagt es, wenn es nicht
     klappt.
  2. Wer zur Fraktion gehört, kommt ebenfalls vom Server — aus den wirklichen
     Konten. Erfundene Mitglieder gibt es nicht mehr.
  3. Was der Nutzer anklickt — Meinungsbild, Markierungen, Notizen,
     Chatbeiträge, Teilnahme, Aufgaben —, liegt im Speicher des Browsers
     (localStorage) und überlebt ein Neuladen der Seite. Der
     Zurücksetzen-Knopf löscht das wieder.

  Wichtig: Gespeichert werden ausschließlich die *Änderungen* des Nutzers,
  nicht der komplette Datenbestand. Sonst würden bei einer Vorführung in zwei
  Wochen noch die Termine von heute im Speicher liegen. Fristen werden bei
  jedem Start frisch gerechnet und mit den gespeicherten Änderungen
  zusammengeführt.
*/

const SPEICHERSCHLUESSEL = "fraktionsbuero-demo-v1";

const LEER = {
  version: 8,
  kanalStumm: {},        // kanalId -> true, wenn der Kanal leise gestellt ist
  kanalGelesen: {},      // kanalId -> Zeitpunkt des letzten Öffnens (ISO)
  /*
    Was hier bleibt, ist ausschließlich persönlich und geräteeigen:

      gelesenExtra  — welche Vorlage ich gelesen habe. Eine Notiz an mich
                      selbst; niemand sonst sieht sie.
      kanalStumm    — welche Kanäle ich leise gestellt habe.
      kanalGelesen  — wann ich einen Kanal zuletzt geöffnet habe.

    Alles andere — Meinungsbild, Teilnahme, Aufgaben, Notizen — liegt auf dem
    Server, damit die Fraktion es gemeinsam sieht.
  */
  gelesenExtra: [],      // dateiId
};

function lade() {
  try {
    const roh = window.localStorage.getItem(SPEICHERSCHLUESSEL);
    if (!roh) return LEER;
    const gelesen = JSON.parse(roh);
    if (gelesen.version !== LEER.version) return LEER;
    return { ...LEER, ...gelesen };
  } catch {
    return LEER;
  }
}

function sichere(aenderungen) {
  try {
    window.localStorage.setItem(SPEICHERSCHLUESSEL, JSON.stringify(aenderungen));
  } catch {
    /* Privater Modus o. Ä. — die Demo läuft dann eben ohne Speicherung weiter. */
  }
}

const Zusammenhang = createContext(null);

export function DemoZustandProvider({ children }) {
  const { token, konto } = useAnmeldung();
  const [aenderungen, setzeAenderungen] = useState(lade);
  const [stammdaten, setzeStammdaten] = useState(null);

  /*
    Markierungen und Notizen liegen jetzt auf dem Server, nicht mehr im
    Browser. Damit sieht jedes Mitglied, was die anderen an den Rand
    geschrieben haben — vorher war das für jeden ein eigener, unsichtbarer
    Stapel.
  */
  const [notizen, setzeNotizen] = useState([]);

  /*
    Meinungsbild, Teilnahmezusagen und Aufgaben — ebenfalls vom Server. Damit
    sieht die Fraktion vor der Sitzung, wer wo steht, wer kommt und was noch
    offen ist. Vorher wusste das jedes Gerät nur von sich selbst.
  */
  const [arbeit, setzeArbeit] = useState({
    meinungsbilder: [],
    teilnahmen: [],
    aufgaben: [],
    spalten: [],
    termine: [],
    /*
      Die abgelegten Dateien der Fraktion. Sie lagen bisher nur in der
      Dateiansicht — für Verknüpfungen an Terminen müssen sie überall
      auflösbar sein. Nicht zu verwechseln mit `stammdaten.dateien`: das
      sind die Volltexte der Vorlagen.
    */
    ablage: [],
  });
  const [ladefehler, setzeLadefehler] = useState(null);
  const [versuch, setzeVersuch] = useState(0);

  useEffect(() => {
    sichere(aenderungen);
  }, [aenderungen]);

  /*
    Vom Server kommen zwei Dinge: was die Verwaltung veröffentlicht hat, und
    wer zur Fraktion gehört. Beides gleichzeitig, nicht nacheinander — auf
    einem Tablet im Mobilfunknetz ist das der Unterschied zwischen einer und
    zwei Wartezeiten.
  */
  useEffect(() => {
    if (!token || !konto) return undefined;
    let abgebrochen = false;

    setzeLadefehler(null);
    Promise.all([
      server.holeVerwaltungsdaten(token),
      server.holeMitglieder(token),
      server.holeNotizen(token),
      server.holeArbeit(token),
      server.holeDateien(token),
    ])
      .then(([daten, mitglieder, geholteNotizen, geholteArbeit, ablage]) => {
        if (abgebrochen) return;
        const ergebnis = baueStammdaten({ ...daten, mitglieder, ichId: konto.id });
        if (ergebnis.fehler) setzeLadefehler(ergebnis.fehler);
        else {
          setzeStammdaten(ergebnis.stammdaten);
          setzeNotizen(geholteNotizen);
          setzeArbeit({ ...geholteArbeit, ablage });
        }
      })
      .catch((fehler) => {
        if (!abgebrochen) setzeLadefehler(fehler.message);
      });

    return () => {
      abgebrochen = true;
    };
  }, [token, konto, versuch]);

  const aendere = useCallback((fn) => {
    setzeAenderungen((bisher) => fn(bisher));
  }, []);

  const ichId = konto ? konto.id : null;

  const aktionen = useMemo(
    () => ({
      /*
        Das Meinungsbild geht an den Server, damit die Fraktion es sieht — es
        ist ausdrücklich namentlich sichtbar. Wer schon eine Position hatte,
        ändert sie; es gibt nie zwei zu demselben Punkt.
      */
      async setzeMeinungsbild(topId, position) {
        const vorhanden = arbeit.meinungsbilder.find(
          (m) => m.topId === topId && m.personId === ichId
        );
        const satz = await server.setzeMeinungsbildAufServer(token, {
          id: vorhanden ? vorhanden.id : null,
          fraktionId: konto.fraktion,
          kontoId: konto.id,
          topId,
          position,
        });
        setzeArbeit((b) => ({
          ...b,
          meinungsbilder: [
            ...b.meinungsbilder.filter((m) => !(m.topId === topId && m.personId === ichId)),
            satz,
          ],
        }));
      },

      /*
        Zurückziehen löscht den Satz wirklich. Früher musste hier ein
        ausdrückliches „null“ gespeichert werden, weil sonst eine Position
        aus den Beispieldaten wieder zum Vorschein kam — die Beispieldaten
        gibt es nicht mehr, also darf es auch die Umständlichkeit nicht mehr
        geben.
      */
      async entferneMeinungsbild(topId) {
        const vorhanden = arbeit.meinungsbilder.find(
          (m) => m.topId === topId && m.personId === ichId
        );
        if (!vorhanden) return;
        await server.entferneMeinungsbildVomServer(token, vorhanden.id);
        setzeArbeit((b) => ({
          ...b,
          meinungsbilder: b.meinungsbilder.filter((m) => m.id !== vorhanden.id),
        }));
      },

      async setzeTeilnahme(sitzungId, status) {
        const vorhanden = arbeit.teilnahmen.find(
          (t) => t.sitzungId === sitzungId && t.personId === ichId
        );
        const satz = await server.setzeTeilnahmeAufServer(token, {
          id: vorhanden ? vorhanden.id : null,
          fraktionId: konto.fraktion,
          kontoId: konto.id,
          sitzungId,
          status,
        });
        setzeArbeit((b) => ({
          ...b,
          teilnahmen: [
            ...b.teilnahmen.filter((t) => !(t.sitzungId === sitzungId && t.personId === ichId)),
            satz,
          ],
        }));
      },

      /*
        Stumm heißt leise, nicht versteckt: Der Kanal bleibt in der Liste,
        behält seine Beiträge und zeigt sie beim Öffnen vollständig. Eine
        Fraktion, in der die Hälfte etwas nicht mitbekommt und es niemandem
        auffällt, wäre schlimmer als eine laute.
      */
      setzeKanalStumm(kanalId, stumm) {
        aendere((b) => ({
          ...b,
          kanalStumm: { ...b.kanalStumm, [kanalId]: Boolean(stumm) },
        }));
      },

      /* Kanal geöffnet — ab hier gilt alles Ältere als gesehen. */
      markiereKanalGelesen(kanalId) {
        aendere((b) => ({
          ...b,
          kanalGelesen: { ...b.kanalGelesen, [kanalId]: new Date().toISOString() },
        }));
      },

      markiereGelesen(dateiId) {
        aendere((b) =>
          b.gelesenExtra.includes(dateiId)
            ? b
            : { ...b, gelesenExtra: [...b.gelesenExtra, dateiId] }
        );
      },

      /*
        Eine neue Markierung — mit oder ohne Notiz.

        Sie geht sofort an den Server, damit die anderen sie sehen. Die
        Antwort kommt mit der endgültigen Kennung zurück und wandert in die
        Liste; solange das läuft, ist die Stelle im Text noch nicht farbig.
        Ein Zwischenzustand mit einer erfundenen Kennung wäre gefährlicher:
        Er müsste später umgehängt werden, und genau daran zerbrechen die
        Verweise aus dem Chat.
      */
      async fuegeAnnotationHinzu({ dateiId, absatzId, start, ende, zitat, sichtbarkeit, farbe, notiz, verankerung }) {
        const satz = await server.legeNotizAn(token, {
          fraktionId: konto.fraktion,
          kontoId: konto.id,
          notiz: { dateiId, absatzId, start, ende, zitat, sichtbarkeit, farbe, notiz, verankerung },
        });
        setzeNotizen((bisher) => [...bisher, satz]);
        return satz.id;
      },

      /*
        Aus einer privaten Markierung wird eine der Fraktion. Passiert, sobald
        jemand die Stelle weitergibt — ohne das stünde im Chat ein Zitat,
        dessen Textstelle die anderen im Dokument gar nicht sehen.
      */
      async macheAnnotationSichtbar(id) {
        const satz = await server.aendereNotiz(token, id, { sichtbarkeit: "fraktion" });
        setzeNotizen((bisher) => bisher.map((n) => (n.id === id ? satz : n)));
      },

      async setzeAnnotationFarbe(id, farbe) {
        const satz = await server.aendereNotiz(token, id, { farbe });
        setzeNotizen((bisher) => bisher.map((n) => (n.id === id ? satz : n)));
      },

      /* Der Text, der aus einer Markierung erst eine Notiz macht. */
      async setzeAnnotationNotiz(id, notiz) {
        const satz = await server.aendereNotiz(token, id, { notiz: (notiz || "").trim() });
        setzeNotizen((bisher) => bisher.map((n) => (n.id === id ? satz : n)));
      },

      async entferneAnnotation(id) {
        await server.loescheNotiz(token, id);
        setzeNotizen((bisher) => bisher.filter((n) => n.id !== id));
      },

      /*
        Eine neue Aufgabe.

        Sie entsteht auf zwei Wegen: aus einem Zitat im Dokument oder direkt
        am Brett. Ohne Angabe landet sie in der ersten Spalte, zuständig ist
        man selbst — und damit privat.
      */
      async fuegeAufgabeHinzu({ titel, beschreibung, topId, vorlageId, faellig, spalteId, zustaendigId }) {
        const erste = arbeit.spalten[0];
        const satz = await server.legeAufgabeAn(token, {
          fraktionId: konto.fraktion,
          kontoId: konto.id,
          aufgabe: {
            titel,
            beschreibung,
            topId,
            vorlageId,
            faellig,
            zustaendigId,
            spalteId: spalteId || (erste ? erste.id : null),
          },
        });
        setzeArbeit((b) => ({ ...b, aufgaben: [...b.aufgaben, satz] }));
        return satz.id;
      },

      /*
        Eine Aufgabe ändern — Spalte, Verantwortung, Beobachtung, Frist,
        Etiketten, Checkliste. Alles über denselben Weg, damit es nicht
        sechs Halbwahrheiten darüber gibt, wie eine Aufgabe geändert wird.

        Die Sichtbarkeit folgt daraus, wie viele Menschen an der Aufgabe
        hängen. Das lässt sich aus den geänderten Feldern allein nicht
        ausrechnen — wer die Verantwortung abgibt, ändert die Beobachtung
        nicht mit. Deshalb wird der Datensatz hier zusammengesetzt, wie er
        hinterher aussieht, und mitgegeben.
      */
      async aendereAufgabe(aufgabeId, felder) {
        const bisher = arbeit.aufgaben.find((a) => a.id === aufgabeId);
        const nachher = {
          zustaendigId:
            felder.zustaendigId !== undefined
              ? felder.zustaendigId
              : bisher && bisher.zustaendigId,
          beobachterIds:
            felder.beobachterIds !== undefined
              ? felder.beobachterIds
              : (bisher && bisher.beobachterIds) || [],
        };

        const satz = await server.aendereAufgabe(token, aufgabeId, felder, nachher);
        setzeArbeit((b) => ({
          ...b,
          aufgaben: b.aufgaben.map((a) => (a.id === aufgabeId ? satz : a)),
        }));
        return satz;
      },

      async loescheAufgabe(aufgabeId) {
        await server.loescheAufgabe(token, aufgabeId);
        setzeArbeit((b) => ({
          ...b,
          aufgaben: b.aufgaben.filter((a) => a.id !== aufgabeId),
        }));
      },

      /* ---------- Termine ---------- */

      /*
        Wer einträgt, ist eingeladen — das setzt der Serveraufruf. Sonst
        verlöre man den eigenen Termin in dem Augenblick, in dem man ihn
        anlegt.
      */
      async fuegeTerminHinzu({ titel, beginn, ende, ort, beschreibung, eingeladenIds }) {
        const satz = await server.legeTerminAn(token, {
          fraktionId: konto.fraktion,
          kontoId: konto.id,
          termin: { titel, beginn, ende, ort, beschreibung, eingeladenIds },
        });
        setzeArbeit((b) => ({ ...b, termine: [...b.termine, satz] }));
        return satz.id;
      },

      async aendereTermin(terminId, felder) {
        const satz = await server.aendereTermin(token, terminId, felder);
        setzeArbeit((b) => ({
          ...b,
          termine: b.termine.map((t) => (t.id === terminId ? satz : t)),
        }));
        return satz;
      },

      async loescheTermin(terminId) {
        await server.loescheTermin(token, terminId);
        setzeArbeit((b) => ({
          ...b,
          termine: b.termine.filter((t) => t.id !== terminId),
        }));
      },

      /* ---------- Spalten des Bretts ---------- */

      async fuegeSpalteHinzu(name) {
        const hinten = arbeit.spalten.reduce((max, s) => Math.max(max, s.reihenfolge), -1);
        const satz = await server.legeSpalteAn(token, {
          fraktionId: konto.fraktion,
          name,
          reihenfolge: hinten + 1,
        });
        setzeArbeit((b) => ({ ...b, spalten: [...b.spalten, satz] }));
        return satz.id;
      },

      async aendereSpalte(spalteId, felder) {
        const satz = await server.aendereSpalte(token, spalteId, felder);
        setzeArbeit((b) => ({
          ...b,
          spalten: b.spalten.map((s) => (s.id === spalteId ? satz : s)),
        }));
      },

      /*
        Eine Spalte löschen nimmt die Karten NICHT mit. Sie wandern in die
        erste Spalte — eine Aufgabe, die verschwindet, weil jemand aufgeräumt
        hat, wäre die schlechteste Art, Arbeit zu verlieren.
      */
      async loescheSpalte(spalteId) {
        const erste = arbeit.spalten.find((s) => s.id !== spalteId);
        const betroffen = arbeit.aufgaben.filter((a) => a.spalteId === spalteId);

        for (const aufgabe of betroffen) {
          try {
            await server.aendereAufgabe(token, aufgabe.id, {
              spalteId: erste ? erste.id : null,
            });
          } catch (fehler) {
            /* weiter — die Oberfläche zeigt sie danach in der ersten Spalte */
          }
        }

        await server.loescheSpalte(token, spalteId);
        setzeArbeit((b) => ({
          ...b,
          spalten: b.spalten.filter((s) => s.id !== spalteId),
          aufgaben: b.aufgaben.map((a) =>
            a.spalteId === spalteId ? { ...a, spalteId: erste ? erste.id : null } : a
          ),
        }));
      },

      /*
        Zurücksetzen für den nächsten Vorführtermin.

        Seit Notizen auf dem Server liegen, reicht es nicht mehr, den Browser
        zu leeren — sonst stünden die eigenen Markierungen beim dritten
        Termin noch im Text. Gelöscht werden deshalb auch die eigenen Notizen
        auf dem Server. Fremde bleiben: Sie gehören nicht einem selbst.

        Chatbeiträge bleiben ebenfalls stehen. Sie liegen bei Matrix, und
        Beiträge nachträglich zurückzuziehen ist ein eigener Vorgang, den es
        noch nicht gibt — das steht so auch in der Rückfrage.
      */
      async zuruecksetzen() {
        const meine = konto ? konto.id : null;

        for (const n of notizen.filter((n) => n.verfasserId === meine)) {
          try {
            await server.loescheNotiz(token, n.id);
          } catch (fehler) {
            /* Eine, die schon weg ist, hält den Rest nicht auf. */
          }
        }
        setzeNotizen((bisher) => bisher.filter((n) => n.verfasserId !== meine));

        for (const m of arbeit.meinungsbilder.filter((m) => m.personId === meine)) {
          try {
            await server.entferneMeinungsbildVomServer(token, m.id);
          } catch (fehler) {
            /* weiter */
          }
        }
        for (const auf of arbeit.aufgaben.filter((a) => a.verfasserId === meine)) {
          try {
            await server.loescheAufgabe(token, auf.id);
          } catch (fehler) {
            /* weiter */
          }
        }
        for (const t of arbeit.teilnahmen.filter((t) => t.personId === meine)) {
          try {
            await server.entferneTeilnahme(token, t.id);
          } catch (fehler) {
            /* weiter */
          }
        }
        setzeArbeit((b) => ({
          meinungsbilder: b.meinungsbilder.filter((m) => m.personId !== meine),
          teilnahmen: b.teilnahmen.filter((t) => t.personId !== meine),
          aufgaben: b.aufgaben.filter((auf) => auf.verfasserId !== meine),
        }));

        try {
          window.localStorage.removeItem(SPEICHERSCHLUESSEL);
        } catch {
          /* nichts zu tun */
        }
        setzeAenderungen(LEER);
      },
    }),
    [aendere, ichId, token, konto, notizen, arbeit]
  );

  /*
    Der Ausgangszustand hängt am Sitzungstermin: Die Fristen der Aufgaben
    werden daraus gerechnet. Solange der Termin nicht da ist, gibt es auch
    keinen Zustand.
  */
  const ausschussBeginn = stammdaten
    ? stammdaten.sitzungen[stammdaten.ausschussId].beginn
    : null;

  const zustand = useMemo(
    () => (ausschussBeginn ? leiteAb(aenderungen, ausschussBeginn, stammdaten, ichId, notizen, arbeit) : null),
    [aenderungen, ausschussBeginn, stammdaten, ichId, notizen, arbeit]
  );

  const wert = useMemo(
    () =>
      stammdaten && zustand
        ? {
            stammdaten,
            ich: stammdaten.personen[stammdaten.ichId],
            zustand,
            aktionen,
            /*
              Auch eigene Notizen zählen: Sonst stünde „Nichts
              zurückzusetzen“, obwohl im Dokument noch die Markierungen des
              letzten Termins stehen.
            */
            /*
              Auch das Eigene auf dem Server zählt: Sonst stünde „Nichts
              zurückzusetzen“, obwohl im Dokument noch die Markierungen des
              letzten Termins stehen und das Meinungsbild abgegeben ist.
            */
            etwasVeraendert:
              istVeraendert(aenderungen) ||
              notizen.some((n) => n.verfasserId === stammdaten.ichId) ||
              arbeit.meinungsbilder.some((m) => m.personId === stammdaten.ichId) ||
              arbeit.teilnahmen.some((t) => t.personId === stammdaten.ichId) ||
              arbeit.aufgaben.some((auf) => auf.verfasserId === stammdaten.ichId),
          }
        : null,
    [stammdaten, zustand, aktionen, aenderungen, notizen, arbeit]
  );

  if (ladefehler) {
    return (
      <Ladefehler
        meldung={ladefehler}
        aufErneut={() => {
          setzeLadefehler(null);
          setzeVersuch((n) => n + 1);
        }}
      />
    );
  }

  if (!wert) {
    return (
      <div className="warten">
        <p>Einen Moment — Sitzung und Vorlagen werden geladen.</p>
      </div>
    );
  }

  return <Zusammenhang.Provider value={wert}>{children}</Zusammenhang.Provider>;
}

/*
  Ohne Sitzung und Tagesordnung lässt sich nichts anzeigen. Das ehrlich zu
  sagen ist besser als eine leere Startseite, bei der man rätselt, ob die
  Fraktion nichts zu tun hat oder die Verbindung fehlt.
*/
function Ladefehler({ meldung, aufErneut }) {
  return (
    <div className="warten">
      <div className="karte" style={{ maxWidth: "32rem" }}>
        <p style={{ fontWeight: 600 }}>Sitzung und Vorlagen ließen sich nicht laden.</p>
        <p className="klein leise" style={{ marginTop: "var(--luft-2)" }}>
          {meldung}
        </p>
        <button className="knopf" style={{ marginTop: "var(--luft-4)" }} onClick={aufErneut}>
          Erneut versuchen
        </button>
      </div>
    </div>
  );
}

export function useDemo() {
  const wert = useContext(Zusammenhang);
  if (!wert) throw new Error("useDemo muss innerhalb von DemoZustandProvider verwendet werden.");
  return wert;
}

/* ---------------------------------------------------------------
   Beispieldaten und gespeicherte Änderungen zusammenführen
   --------------------------------------------------------------- */

function istVeraendert(a) {
  return (
    a.gelesenExtra.length > 0 ||
    Object.keys(a.kanalStumm).length > 0 ||
    Object.keys(a.kanalGelesen).length > 0
  );
}

function leiteAb(a, ausschussBeginn, stammdaten, ichId, annotationen, arbeit) {
  /* Meinungsbilder kommen vollständig vom Server — auch die der anderen. */
  const meinungsbilder = arbeit.meinungsbilder;

  /*
    Teilnahmen: Für jede Sitzung bekommt jedes Mitglied einen Eintrag. Wer
    geantwortet hat, bekommt seine Antwort vom Server; alle übrigen stehen
    mit `null` da. Nur so lässt sich „drei ohne Rückmeldung“ überhaupt sagen —
    ein fehlender Satz wäre nicht von einem fehlenden Mitglied zu
    unterscheiden.
  */
  const mitgliederIds = Object.keys((stammdaten && stammdaten.personen) || {});
  const sitzungsIds = Object.keys((stammdaten && stammdaten.sitzungen) || {});

  const geantwortet = {};
  arbeit.teilnahmen.forEach((t) => {
    geantwortet[t.sitzungId + "|" + t.personId] = t.status;
  });

  const teilnahmen = [];
  sitzungsIds.forEach((sitzungId) => {
    mitgliederIds.forEach((personId) => {
      const antwort = geantwortet[sitzungId + "|" + personId];
      teilnahmen.push({
        sitzungId,
        personId,
        status: antwort === undefined ? null : antwort,
      });
    });
  });

  /* Chatbeiträge liegen bei Matrix — siehe ChatZustand.js. */
  const nachrichten = [];

  /*
    Ohne eigene Frist gilt die Ausschusssitzung: Eine Aufgabe ohne Datum
    verschwindet sonst aus der Liste „Was bis dahin zu tun ist“.
  */
  /*
    Was „erledigt“ heißt, sagt die Spalte — nicht die Aufgabe. Mit frei
    benennbaren Spalten gibt es kein festes „erledigt“ mehr; stattdessen
    trägt die Spalte die Angabe, und die Aufgabe erbt sie.
  */
  const fertigeSpalten = new Set(
    (arbeit.spalten || []).filter((s) => s.erledigt).map((s) => s.id)
  );

  const aufgaben = arbeit.aufgaben.map((auf) => ({
    ...auf,
    faellig: auf.faellig || new Date(ausschussBeginn),
    erledigt: fertigeSpalten.has(auf.spalteId),
  }));

  const gelesen = Array.from(new Set(a.gelesenExtra));

  return {
    meinungsbilder,
    teilnahmen,
    annotationen,
    nachrichten,
    aufgaben,
    spalten: arbeit.spalten || [],
    termine: arbeit.termine || [],
    ablage: arbeit.ablage || [],
    gelesen,
    kanalStumm: a.kanalStumm,
    kanalGelesen: a.kanalGelesen,
  };
}
